import {
  type FC,
  type ReactNode,
  useCallback,
  useEffect,
  useMemo,
} from "react";
import {
  BrowserRouter,
  Navigate,
  Route,
  Routes,
  useLocation,
} from "react-router-dom";

import DashboardLogin from "./components/auth/DashboardLogin";
import { Alert } from "./components/common/Alert";
import DashboardView from "./components/DashboardView";
import { InvitePage } from "./components/invite/InvitePage";
import { OnboardingWizard } from "./components/onboarding";
import { PublicBillingPage } from "./components/public/PublicBillingPage";
import { PublicLandingPage } from "./components/public/PublicLandingPage";
import { ThemeProvider } from "./context/ThemeContext";
import { ToastProvider } from "./context/ToastContext";
import {
  DashboardAuthProvider,
  useDashboardAuth,
} from "./hooks/useDashboardAuth";
import { useDashboardData } from "./hooks/useDashboardData";
import { useOrganizationMembershipRefresh } from "./hooks/useOrganizationMembershipRefresh";
import { usePageMetadata } from "./hooks/usePageMetadata";

const DASHBOARD_ROUTE_PATHS = [
  "/overview",
  "/usage",
  "/flows",
  "/plans",
  "/agents-instances",
  "/agents-templates",
  "/repo-chat",
  "/integrations",
  "/settings",
  "/admin",
  "/wiki",
  "/release-notes",
] as const;

const REDIRECT_ROUTES = [
  { path: "/plan-reviews", to: "/plans?tab=revisions" },
  { path: "/plan-snapshots", to: "/plans" },
  { path: "/issue-integrations", to: "/integrations" },
  { path: "/source-control", to: "/integrations?tab=source-control" },
  { path: "/source-control/chat", to: "/repo-chat" },
  { path: "/source-control/indexing", to: "/integrations?tab=indexing" },
  { path: "/schema-telemetry", to: "/settings?tab=telemetry" },
  { path: "/agent-budgets", to: "/settings" },
  { path: "/agents", to: "/agents-instances" },
  { path: "/templates", to: "/agents-templates" },
  { path: "/admin/feature-flags", to: "/admin?tab=feature-flags" },
] as const;

const DashboardErrorBanner: FC<{ error: string }> = ({ error }) => (
  <div className="mx-auto mt-6 w-full max-w-4xl">
    <Alert variant="error">{error}</Alert>
  </div>
);

const DashboardLoadingFallback: FC = () => (
  <div className="flex h-[60vh] flex-col items-center justify-center gap-4 text-center">
    <img src="/arti.svg" alt="Arti" className="h-10 w-10 animate-pulse" />
    <div className="space-y-1">
      <p className="font-mono text-[11px] font-bold uppercase tracking-widest text-slate-900 dark:text-white">
        Loading dashboard…
      </p>
      <p className="max-w-xs text-xs text-gray-400 dark:text-white/40">
        Fetching orchestrator and node telemetry.
      </p>
    </div>
  </div>
);

const AuthenticatedLoginRedirect: FC<{ defaultRedirect: string }> = ({
  defaultRedirect,
}) => {
  const location = useLocation();
  const redirectTarget =
    getSafeRedirectTarget(location.search) ?? defaultRedirect;

  useEffect(() => {
    window.location.replace(redirectTarget);
  }, [redirectTarget]);

  return <DashboardLoadingFallback />;
};

const AuthenticatedRoutes: FC<{
  dashboardDataEnabled: boolean;
  dashboardElement: ReactNode;
  defaultRedirect: string;
  hasData: boolean;
}> = ({ dashboardDataEnabled, dashboardElement, defaultRedirect, hasData }) => (
  <Routes>
    <Route
      path="/login"
      element={<AuthenticatedLoginRedirect defaultRedirect={defaultRedirect} />}
    />
    <Route path="/onboarding" element={<OnboardingWizard />} />
    <Route path="/invite" element={<InvitePage />} />
    <Route path="/" element={<Navigate to={defaultRedirect} replace />} />
    {hasData ? (
      <>
        {DASHBOARD_ROUTE_PATHS.map((path) => (
          <Route key={path} path={path} element={dashboardElement} />
        ))}
        {REDIRECT_ROUTES.map((route) => (
          <Route
            key={route.path}
            path={route.path}
            element={<Navigate to={route.to} replace />}
          />
        ))}
      </>
    ) : (
      <Route
        path="*"
        element={
          dashboardDataEnabled ? (
            <DashboardLoadingFallback />
          ) : (
            <Navigate to={defaultRedirect} replace />
          )
        }
      />
    )}
    <Route path="*" element={<Navigate to={defaultRedirect} replace />} />
  </Routes>
);

const UnauthenticatedRoutes: FC<{
  startOtp: (email: string) => Promise<void>;
  finishOtp: (
    email: string,
    code: string,
    rememberDevice: boolean,
  ) => Promise<void>;
}> = ({ startOtp, finishOtp }) => (
  <Routes>
    <Route
      path="/invite"
      element={
        <DashboardLogin onRequestOtp={startOtp} onVerifyOtp={finishOtp} />
      }
    />
    <Route
      path="/login"
      element={
        <DashboardLogin onRequestOtp={startOtp} onVerifyOtp={finishOtp} />
      }
    />
    <Route
      path="/billing"
      element={
        <PublicBillingPage onRequestOtp={startOtp} onVerifyOtp={finishOtp} />
      }
    />
    <Route path="*" element={<PublicLandingPage />} />
  </Routes>
);

function renderAppBody(
  authStatus: "loading" | "unauthenticated" | "authenticated",
  startOtp: (email: string) => Promise<void>,
  finishOtp: (
    email: string,
    code: string,
    rememberDevice: boolean,
  ) => Promise<void>,
  authenticatedContent: ReactNode,
  unauthenticatedContent: ReactNode,
): ReactNode {
  if (authStatus === "loading") {
    return (
      <div className="flex h-[60vh] flex-col items-center justify-center gap-4 text-center">
        <img src="/arti.svg" alt="Arti" className="h-10 w-10 animate-pulse" />
        <div className="space-y-1">
          <p className="font-mono text-[11px] font-bold uppercase tracking-widest text-slate-900 dark:text-white">
            Checking access…
          </p>
          <p className="text-xs text-gray-400 dark:text-white/40">
            Authenticating your session
          </p>
        </div>
      </div>
    );
  }

  if (authStatus !== "authenticated") {
    return unauthenticatedContent;
  }

  return authenticatedContent;
}

const AppContent: FC = () => {
  const auth = useDashboardAuth();
  usePageMetadata(
    auth.status === "authenticated"
      ? {
          description:
            "Secure workspace for Arti customers to monitor execution, telemetry, plan revisions, and usage.",
          robots: "noindex,nofollow",
          title: "Arti Dashboard",
          type: "website",
        }
      : null,
  );
  const dashboardDataEnabled =
    auth.status === "authenticated" && Boolean(auth.organizationId);
  useOrganizationMembershipRefresh({
    enabled: auth.status === "authenticated",
    refreshSession: auth.refreshSession,
    userId: auth.userId,
  });
  const { data, loading, error, refresh, realtime } = useDashboardData({
    enabled: dashboardDataEnabled,
    onUnauthorized: auth.markUnauthorized,
    userId: auth.userId,
  });
  const handleRefresh = useCallback(() => {
    void refresh();
  }, [refresh]);
  const handleLogout = useCallback(() => {
    void auth.logout();
  }, [auth]);

  const dashboardElement = useMemo(
    () =>
      data ? (
        <DashboardView
          data={data}
          loading={loading}
          onRefresh={handleRefresh}
          onLogout={handleLogout}
          organizationId={auth.organizationId}
          realtime={realtime}
        />
      ) : null,
    [auth.organizationId, data, loading, handleRefresh, handleLogout, realtime],
  );

  const defaultRedirect = auth.organizationId ? "/overview" : "/onboarding";

  return (
    <div className="min-h-screen bg-slate-50 text-slate-900 dark:bg-arti-background dark:text-slate-100">
      {renderAppBody(
        auth.status,
        auth.startOtp,
        auth.finishOtp,
        <>
          {error ? <DashboardErrorBanner error={error} /> : null}
          <AuthenticatedRoutes
            dashboardDataEnabled={dashboardDataEnabled}
            dashboardElement={dashboardElement}
            defaultRedirect={defaultRedirect}
            hasData={Boolean(data)}
          />
        </>,
        <UnauthenticatedRoutes
          startOtp={auth.startOtp}
          finishOtp={auth.finishOtp}
        />,
      )}
    </div>
  );
};

const App: FC = () => (
  <BrowserRouter>
    <ThemeProvider>
      <ToastProvider>
        <DashboardAuthProvider>
          <AppContent />
        </DashboardAuthProvider>
      </ToastProvider>
    </ThemeProvider>
  </BrowserRouter>
);

export default App;

function getSafeRedirectTarget(search: string): string | null {
  const params = new URLSearchParams(search);
  const redirectTo = params.get("redirect_to");
  if (
    !redirectTo ||
    !redirectTo.startsWith("/") ||
    redirectTo.startsWith("//")
  ) {
    return null;
  }
  return redirectTo;
}
